import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Subject, Subscription, takeUntil, forkJoin, debounceTime, map, merge, take, finalize, of, catchError, switchMap, pairwise, startWith, distinctUntilChanged } from 'rxjs';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { TextareaModule } from 'primeng/textarea';
import { MessageService } from 'primeng/api';
import { OrdenService } from '../../../services/orden/orden.service';
import { OrdenItemService } from '../../../services/orden-item/orden-item.service';
import { ClienteLookupService } from '../../../services/cliente/cliente-lookup.service';
import { TarifaService } from '../../../services/tarifa/tarifa.service';
import { ClientPricingService } from '../../../services/pricing/client-pricing.service';
import { Orden, OrdenCliente, OrdenEstado, OrdenItem } from '../../../domain/orden.domain';
import { Cliente } from '../../../domain/cliente.domain';
import { Tarifa } from '../../../domain/tarifa.domain';
import { ItemsEmisionSectionComponent } from '../../../components/ordenes/items-emision/items-emision-section.component';
import { ItemsEmisionFacadeService } from '../../../components/ordenes/items-emision/items-emision-facade.service';
import { CurrencyFormatPipe } from '../../../pipes/currency-format.pipe';
import { ApiErrorService } from '../../../core/errors/api-error.service';
import { NormalizedFormError } from '../../../core/errors/api-error.model';
import { environment } from '../../../../environments/environment';

type ClienteRolKey = 'principal' | 'anunciante' | 'productor';
type ClienteComboKey = 'clientePrincipal' | 'anunciante' | 'productor';

@Component({
  selector: 'app-orden-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    AutoCompleteModule,
    SelectModule,
    DatePickerModule,
    TextareaModule,
    ItemsEmisionSectionComponent,
    CurrencyFormatPipe
  ],
  providers: [ItemsEmisionFacadeService],
  templateUrl: './orden-edit.component.html',
  styleUrls: ['./orden-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrdenEditComponent implements OnInit, OnDestroy {
  @ViewChild('formContainer') formContainer?: ElementRef<HTMLElement>;
  @ViewChild(ItemsEmisionSectionComponent) itemsSection?: ItemsEmisionSectionComponent;

  ordenForm!: FormGroup;
  loading = signal<boolean>(false);
  itemsLoading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  ordenId: string | null = null;
  validationSummary = signal<string[]>([]);

  clientePrincipalOptions: Cliente[] = [];
  anuncianteOptions: Cliente[] = [];
  productorOptions: Cliente[] = [];
  clientePrincipalSuggestions: Cliente[] = [];
  anuncianteSuggestions: Cliente[] = [];
  productorSuggestions: Cliente[] = [];

  loadingClientes: Record<ClienteComboKey, boolean> = {
    clientePrincipal: false,
    anunciante: false,
    productor: false
  };
  loadedOnce: Record<ClienteComboKey, boolean> = {
    clientePrincipal: false,
    anunciante: false,
    productor: false
  };
  
  // Subjects para debounce en búsquedas
  private clientePrincipalSearch$ = new Subject<string>();
  private anuncianteSearch$ = new Subject<string>();
  private productorSearch$ = new Subject<string>();

  totals = signal({
    duracionTotal: 0,
    bruto: 0,
    descuento: 0,
    descuentoMonto: 0,
    neto: 0,
    comision: 0
  });
  confirmedTotals = signal<{ total: number; duracionTotal: number } | null>(null);

  private destroy$ = new Subject<void>();
  private ordenClienteIds: Record<string, number> = {};

  tarifaOptions = signal<Tarifa[]>([]);
  private tarifaFromOrden: Tarifa | null = null;
  private tarifaCompleta: Tarifa | null = null;
  private itemsRecalculationSubscription?: Subscription;

  tipoOrdenOptions = [
    { label: 'Emite y factura', value: 'Emite y factura' },
    { label: 'Sólo factura', value: 'Sólo factura' },
    { label: 'Sólo emite', value: 'Sólo emite' }
  ];

  estadoOptions: { label: string; value: OrdenEstado }[] = [
    { label: 'Borrador', value: 'borrador' },
    { label: 'Confirmada', value: 'confirmada' },
    { label: 'Anulada', value: 'anulada' }
  ];

  monedaOptions = [
    { label: 'Pesos', value: 'Pesos' },
    { label: 'Dólar', value: 'Dólar' },
    { label: 'Euro', value: 'Euro' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private ordenService: OrdenService,
    private clienteLookupService: ClienteLookupService,
    private tarifaService: TarifaService,
    private itemsEmisionFacade: ItemsEmisionFacadeService,
    private messageService: MessageService,
    private apiErrorService: ApiErrorService,
    private cdr: ChangeDetectorRef
  ) {}

  private readonly ordenItemService = inject(OrdenItemService);
  private readonly clientPricingService = inject(ClientPricingService);

  ngOnInit(): void {
    this.initForm();
    this.setupRouteParamListener();
    this.loadTarifas();
    this.setupSearchDebounce();
    this.preloadClientes();
    this.setupTarifaWatcher();
    this.setupItemsRecalculation();
  }

  ngOnDestroy(): void {
    this.itemsRecalculationSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get itemsFormArray(): FormArray {
    return this.ordenForm.get('items') as FormArray;
  }

  initForm(): void {
    this.ordenForm = this.fb.group({
      numero: ['', [Validators.required, Validators.maxLength(255)]],
      fechaIngreso: [new Date(), [Validators.required]],
      clientePrincipal: [null, [Validators.required]],
      anunciante: [null],
      productor: [null],
      facturacionRol: ['principal', [Validators.required]],
      tipoOrden: [null, [Validators.required]],
      estado: ['borrador', [Validators.required]],
      moneda: ['Pesos', [Validators.required]],
      tarifa: [null],
      vigenciaDesde: [null],
      vigenciaHasta: [null],
      descuento: [0],
      porcentajeComision: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      esqueletoNombre: [null],
      observaciones: [null],
      items: this.fb.array([])
    });

    this.ordenForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateTotals();
    });

    const productorId$ = this.ordenForm.get('productor')?.valueChanges.pipe(
      map(value => this.extractId(value) ?? null),
      distinctUntilChanged()
    );
    const clientePrincipalId$ = this.ordenForm.get('clientePrincipal')?.valueChanges.pipe(
      map(value => this.extractId(value) ?? null),
      distinctUntilChanged()
    );

    if (productorId$ && clientePrincipalId$) {
      merge(productorId$, clientePrincipalId$)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadedOnce.anunciante = false;
          this.refreshAnunciantes();
        });
    }

    const facturacionTriggers = [
      this.ordenForm.get('clientePrincipal')?.valueChanges,
      this.ordenForm.get('anunciante')?.valueChanges,
      this.ordenForm.get('productor')?.valueChanges
    ].filter(Boolean);

    if (facturacionTriggers.length) {
      merge(...(facturacionTriggers as any))
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.ensureFacturacionSelection());
    }

    const facturacionRolControl = this.ordenForm.get('facturacionRol');
    facturacionRolControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((rol) => {
        const ordenClienteId = rol ? this.ordenClienteIds[rol] : null;
        console.debug(`[ORDEN_UI_FACTURACION] orden=${this.ordenId ?? 'new'} set facturado rol=${rol ?? 'NONE'} ordenClienteId=${ordenClienteId ?? 'null'}`);
      });

    this.ordenForm.get('estado')?.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((estado: OrdenEstado | null) => {
        this.syncTarifaControlState(estado ?? null);
      });
  }

  /**
   * Configura el observador para cambios en el campo tarifa
   * Cuando cambia la tarifa, carga los detalles completos y recalcula todos los items
   */
  private setupTarifaWatcher(): void {
    this.ordenForm.get('tarifa')?.valueChanges
      .pipe(
        distinctUntilChanged(),
        switchMap((tarifaId: string | number | null) => {
          if (!tarifaId) {
            this.tarifaCompleta = null;
            this.clearPricingPreview('Tarifa no especificada');
            return of(null);
          }
          return this.tarifaService.getTarifaWithDetalles(tarifaId).pipe(
            catchError(error => {
              console.error('Error al cargar tarifa completa:', error);
              this.messageService.add({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'No se pudo cargar el tarifario completo.',
                life: 3000
              });
              this.tarifaCompleta = null;
              return of(null);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(tarifa => {
        this.tarifaCompleta = tarifa;
        if (tarifa?.tarifaDetalles?.length) {
          this.recalculateAllItems();
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Configura el observador para cambios en los items
   * Recalcula precios con debounce para evitar cálculos excesivos
   */
  private setupItemsRecalculation(): void {
    this.itemsRecalculationSubscription = this.itemsFormArray.valueChanges
      .pipe(
        startWith(this.itemsFormArray.value),
        pairwise(),
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(([prev, curr]) => {
        const prevPricing = JSON.stringify(this.extractPricingFields(prev));
        const currPricing = JSON.stringify(this.extractPricingFields(curr));
        if (prevPricing !== currPricing) {
          this.recalculateAllItems();
        } else {
          this.itemsEmisionFacade.refreshSubtotals();
          this.updateTotals();
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Extrae solo los campos relevantes para pricing de los items
   * Usado para detectar cambios significativos y evitar recálculos innecesarios
   */
  private extractPricingFields(items: any[]): any[] {
    return items.map(item => ({
      tipoProducto: item.tipoProducto?.id,
      programa: item.programa?.id,
      material: item.material?.id,
      diaTipo: item.diaTipo,
      frecuencia: item.frecuencia,
      horaDesde: item.horaDesde,
      horaHasta: item.horaHasta,
      avisos: item.avisos,
      cantidad: item.cantidad,
      duracionSegundos: item.duracionSegundos,
      bonificado: item.bonificado,
      formatoDigital: item.formatoDigital?.id,
      ubicacionDigital: item.ubicacionDigital?.id,
      plataformaRedes: item.plataformaRedes,
      fechaDesde: item.fechaDesde,
      fechaHasta: item.fechaHasta
    }));
  }

  /**
   * Recalcula los precios de todos los items usando el ClientPricingService
   * Este es el método principal que aplica la lógica de cálculo en tiempo real
   */
  private recalculateAllItems(): void {
    if (!this.tarifaCompleta?.tarifaDetalles?.length) {
      return;
    }

    const vigenciaDesde = this.ordenForm.get('vigenciaDesde')?.value;
    const vigenciaHasta = this.ordenForm.get('vigenciaHasta')?.value;

    this.itemsFormArray.controls.forEach(control => {
      const itemGroup = control as FormGroup;
      const itemValue = itemGroup.value;
      const tipoCodigo = this.getTipoProductoCodigo(itemValue.tipoProducto);
      const itemRef = itemValue.id ?? itemValue.clientRowId ?? itemValue.ordenIndex ?? '-';
      const frecuencia = itemValue.frecuencia ?? null;

      // Construir OrdenItem para cálculo
      const ordenItem: Partial<OrdenItem> = {
        ...itemValue,
        fechaDesde: itemValue.fechaDesde || vigenciaDesde,
        fechaHasta: itemValue.fechaHasta || vigenciaHasta
      };

      try {
        if (tipoCodigo === 'AIRE_SPOT' && !frecuencia) {
          console.warn(`[ORDEN_PRICING] item=${itemRef} tipo=AIRE_SPOT frecuencia=NONE tarifaDetalle=null reason=missing_frecuencia`);
        }

        const result = this.clientPricingService.calculateItem(ordenItem, this.tarifaCompleta!);

        // Actualizar FormGroup SIN emitir evento (evita loops infinitos)
        itemGroup.patchValue({
          subtotalPreview: result.subtotalPreview,
          duracionSegundosPreview: result.duracionSegundosPreview,
          tarifaDetallePreviewId: result.tarifaDetalleId,
          precioUnitarioPreview: result.precioUnitario,
          unidadTarifacionPreview: result.unidad,
          pricingStatus: result.status,
          pricingTrace: result.trace,
          pricingError: result.errorMessage
        }, { emitEvent: false });

        if (tipoCodigo === 'AIRE_SPOT') {
          const resolvedFreq = frecuencia ?? 'NONE';
          const detalle = result.tarifaDetalleId ?? 'null';
          const reason = result.status ?? 'OK';
          console.log(`[ORDEN_PRICING] item=${itemRef} tipo=AIRE_SPOT frecuencia=${resolvedFreq} tarifaDetalle=${detalle} reason=${reason}`);
        }

        if (environment.pricingDebug) {
          console.debug('[ORDEN_ITEM_OVERRIDE]', {
            tarifaId: this.tarifaCompleta?.id ?? null,
            tarifaDetalleId: result.tarifaDetalleId,
            subtotalCalculado: result.subtotalPreview,
            subtotalManual: itemGroup.get('subtotalManual')?.value ?? null,
            subtotalFinal: this.itemsEmisionFacade.getSubtotal(itemGroup),
            rejections: result.trace?.match?.rejections ?? []
          });
        }

      } catch (error) {
        console.error('Error calculando item:', error);
        itemGroup.patchValue({
          pricingStatus: 'NO_MATCH',
          pricingError: 'Error en cálculo de precio'
        }, { emitEvent: false });
      }
    });

    // Refrescar subtotales por tipo para mantener sincronizada la UI (headers de sección)
    this.itemsEmisionFacade.refreshSubtotals();
    this.cdr.markForCheck();
    this.updateTotals();
  }

  private clearPricingPreview(message: string): void {
    this.itemsFormArray.controls.forEach(control => {
      const itemGroup = control as FormGroup;
      itemGroup.patchValue({
        subtotalPreview: null,
        duracionSegundosPreview: null,
        tarifaDetallePreviewId: null,
        precioUnitarioPreview: null,
        unidadTarifacionPreview: null,
        pricingStatus: 'INCOMPLETE',
        pricingTrace: null,
        pricingError: message
      }, { emitEvent: false });
    });
    // Refrescar subtotales por tipo para mantener sincronizada la UI
    this.itemsEmisionFacade.refreshSubtotals();
    this.updateTotals();
  }

  setupRouteParamListener(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newId = params.get('id');
        if (newId !== this.ordenId) {
          this.loadOrden(newId);
        }
      });
  }

  loadOrden(id: string | null): void {
    this.ordenId = id;
    this.ordenClienteIds = {};
    this.itemsLoading.set(true);
    this.resetFormState();

    if (this.ordenId) {
      this.isEditMode.set(true);
      this.loading.set(true);

      this.ordenService.getById(this.ordenId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (orden: Orden) => {
            this.patchOrdenForm(orden);
            this.loading.set(false);
            this.itemsLoading.set(false);
          },
          error: (error) => {
            console.error('Error al cargar orden:', error);
            this.loading.set(false);
            this.itemsLoading.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo cargar la orden.',
              life: 3000
            });
            this.router.navigate(['/ordenes']);
          }
        });
    } else {
      this.isEditMode.set(false);
      this.itemsLoading.set(false);
    }
  }

  patchOrdenForm(orden: Orden): void {
    this.itemsFormArray.clear();
    const ordenClientes = orden.ordenClientes || [];
    const principal = ordenClientes.find(cliente => cliente.rol === 'principal');
    const anunciante = ordenClientes.find(cliente => cliente.rol === 'anunciante');
    const productor = ordenClientes.find(cliente => cliente.rol === 'productor');

    ordenClientes.forEach(cliente => {
      if (cliente.rol && cliente.id) {
        this.ordenClienteIds[cliente.rol] = cliente.id;
      }
    });

    // Asegurar que los clientes seleccionados estén en las listas de sugerencias
    const promises: Promise<void>[] = [];

    if (principal?.cliente) {
      promises.push(this.ensureClienteInSuggestions(principal.cliente, 'agencia', 'clientePrincipal'));
    }
    if (anunciante?.cliente) {
      promises.push(this.ensureClienteInSuggestions(anunciante.cliente, 'anunciante', 'anunciante'));
    }
    if (productor?.cliente) {
      promises.push(this.ensureClienteInSuggestions(productor.cliente, 'productor', 'productor'));
    }

    // Esperar a que se aseguren los clientes antes de hacer patchValue
    Promise.all(promises).then(() => {
      this.tarifaFromOrden = typeof orden.tarifa === 'object' ? orden.tarifa : null;
      this.ensureTarifaOption(this.tarifaFromOrden);
      if (orden.montoTotal !== null && orden.montoTotal !== undefined
        || orden.duracionTotalSegundos !== null && orden.duracionTotalSegundos !== undefined) {
        this.confirmedTotals.set({
          total: Number(orden.montoTotal ?? 0),
          duracionTotal: Number(orden.duracionTotalSegundos ?? 0)
        });
      } else {
        this.confirmedTotals.set(null);
      }

      this.ordenForm.patchValue({
        numero: orden.numero || '',
        fechaIngreso: orden.fechaIngreso ? new Date(orden.fechaIngreso) : new Date(),
        clientePrincipal: principal?.cliente || null,
        anunciante: anunciante?.cliente || null,
        productor: productor?.cliente || null,
        facturacionRol: this.resolveFacturacionRolDefault(orden, principal, anunciante, productor),
        tipoOrden: orden.tipoOrden || null,
        estado: orden.estado || 'borrador',
        moneda: orden.moneda || 'Pesos',
        tarifa: this.extractId(orden.tarifa),
        vigenciaDesde: orden.vigenciaDesde ? new Date(orden.vigenciaDesde) : null,
        vigenciaHasta: orden.vigenciaHasta ? new Date(orden.vigenciaHasta) : null,
        descuento: orden.descuento ?? 0,
        porcentajeComision: orden.porcentajeComision ?? 0,
        esqueletoNombre: orden.esqueletoNombre ?? null,
        observaciones: orden.observaciones ?? null
      });

      // Si hay productor o agencia, refrescar anunciantes
      if (productor?.cliente || principal?.cliente) {
        setTimeout(() => this.refreshAnunciantes(), 100);
      }

      this.syncTarifaControlState(orden.estado ?? 'borrador');
    });

    (orden.items || []).forEach(item => {
      const normalizedItem = { ...item };
      this.itemsFormArray.push(this.itemsEmisionFacade.createItemGroup(normalizedItem));
    });

    this.updateTotals();
  }

  /**
   * Asegura que un cliente esté en la lista de sugerencias correspondiente
   */
  private async ensureClienteInSuggestions(
    cliente: Cliente | string,
    tipoCliente: 'agencia' | 'anunciante' | 'productor',
    fieldName: 'clientePrincipal' | 'anunciante' | 'productor'
  ): Promise<void> {
    return new Promise((resolve) => {
      // Si el cliente ya es un objeto completo, verificar si está en la lista
      if (typeof cliente === 'object' && cliente !== null) {
        const clienteId = this.extractId(cliente);
        let currentSuggestions: Cliente[] = [];

        if (fieldName === 'clientePrincipal') {
          currentSuggestions = this.clientePrincipalOptions;
        } else if (fieldName === 'anunciante') {
          currentSuggestions = this.anuncianteOptions;
        } else {
          currentSuggestions = this.productorOptions;
        }

        const existe = currentSuggestions.some(c => {
          const cId = this.extractId(c);
          return cId !== null && String(cId) === String(clienteId);
        });
        if (existe) {
          resolve();
          return;
        }

        // Si no existe, agregarlo a la lista
        if (fieldName === 'clientePrincipal') {
          this.clientePrincipalOptions = [...currentSuggestions, cliente];
          this.clientePrincipalSuggestions = [...this.clientePrincipalOptions];
        } else if (fieldName === 'anunciante') {
          this.anuncianteOptions = [...currentSuggestions, cliente];
          this.anuncianteSuggestions = [...this.anuncianteOptions];
        } else {
          this.productorOptions = [...currentSuggestions, cliente];
          this.productorSuggestions = [...this.productorOptions];
        }
        this.cdr.markForCheck();
        resolve();
      } else {
        // Si es un string/IRI, obtener el cliente completo
        const clienteId = this.extractId(cliente);
        if (!clienteId) {
          resolve();
          return;
        }

        this.clienteLookupService.getClienteById(clienteId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (option) => {
              if (option) {
                let currentSuggestions: Cliente[] = [];

                if (fieldName === 'clientePrincipal') {
                  currentSuggestions = this.clientePrincipalOptions;
                  const existe = currentSuggestions.some(c => {
                    const cId = this.extractId(c);
                    return cId !== null && String(cId) === String(option.id);
                  });
                  if (!existe) {
                    this.clientePrincipalOptions = [...currentSuggestions, option.cliente];
                    this.clientePrincipalSuggestions = [...this.clientePrincipalOptions];
                  }
                } else if (fieldName === 'anunciante') {
                  currentSuggestions = this.anuncianteOptions;
                  const existe = currentSuggestions.some(c => {
                    const cId = this.extractId(c);
                    return cId !== null && String(cId) === String(option.id);
                  });
                  if (!existe) {
                    this.anuncianteOptions = [...currentSuggestions, option.cliente];
                    this.anuncianteSuggestions = [...this.anuncianteOptions];
                  }
                } else {
                  currentSuggestions = this.productorOptions;
                  const existe = currentSuggestions.some(c => {
                    const cId = this.extractId(c);
                    return cId !== null && String(cId) === String(option.id);
                  });
                  if (!existe) {
                    this.productorOptions = [...currentSuggestions, option.cliente];
                    this.productorSuggestions = [...this.productorOptions];
                  }
                }
                this.cdr.markForCheck();
              }
              resolve();
            },
            error: () => {
              resolve(); // Continuar aunque falle
            }
          });
      }
    });
  }


  mostrarErrores(): void {
    this.ordenForm.markAllAsTouched();
    const errors = this.collectFormValidationErrors();
    this.validationSummary.set(errors);
    this.messageService.add({
      severity: 'info',
      summary: 'Errores de validación',
      detail: `Hay ${errors.length} campo(s) que requieren atención. Revisá la lista debajo de Ítems de Emisión.`,
      life: 5000
    });
    // Scroll to validation summary
    setTimeout(() => {
      const summaryElement = this.formContainer?.nativeElement.querySelector('.validation-summary');
      if (summaryElement) {
        summaryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  onSubmit(): void {
    this.validationSummary.set([]);
    this.ordenForm.markAllAsTouched();
    if (this.ordenForm.invalid) {
      const errors = this.collectFormValidationErrors();
      this.validationSummary.set(errors);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario inválido',
        detail: 'Completá los campos obligatorios para guardar.',
        life: 3000
      });
      this.apiErrorService.focusFirstInvalid(this.formContainer?.nativeElement);
      return;
    }

    const formValue = this.ordenForm.getRawValue();
    const ordenClientes = this.buildOrdenClientes(formValue);
    const itemsPayload = this.buildItemsPayload();

    const ordenPayload: Orden = {
      numero: formValue.numero,
      fechaIngreso: this.formatDateTime(formValue.fechaIngreso) ?? undefined,
      vigenciaDesde: this.formatDateTime(formValue.vigenciaDesde),
      vigenciaHasta: this.formatDateTime(formValue.vigenciaHasta),
      porcentajeComision: formValue.porcentajeComision,
      descuento: formValue.descuento,
      tipoOrden: formValue.tipoOrden,
      moneda: formValue.moneda,
      tarifa: this.buildIri(formValue.tarifa, 'tarifas') || undefined,
      observaciones: formValue.observaciones,
      estado: formValue.estado || 'borrador',
      esqueletoNombre: formValue.esqueletoNombre,
      ordenClientes
    };

    this.loading.set(true);

    const operation = this.isEditMode()
      ? this.syncOrdenEdicion(ordenPayload, itemsPayload)
      : this.ordenService.create({ ...ordenPayload, items: itemsPayload });

    operation.subscribe({
      next: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: this.isEditMode() ? 'Orden actualizada correctamente' : 'Orden creada correctamente',
          life: 3000
        });
        if (!this.isEditMode()) {
          setTimeout(() => {
            this.router.navigate(['/ordenes']);
          }, 1200);
        }
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al guardar orden:', error);
        if (this.apiErrorService.isValidationError422(error)) {
          this.handleValidationErrors(error);
          return;
        }

        const backendMessage = error?.error?.message || error?.error?.error || error?.message;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: backendMessage || 'No se pudo guardar la orden.',
          life: 5000
        });
      }
    });
  }

  private syncOrdenEdicion(ordenPayload: Orden, itemsPayload: OrdenItem[]) {
    const deletedItemIds = this.itemsEmisionFacade.getDeletedItemIds();
    const { itemsToCreate, itemsToUpdate } = this.splitItemsPayload(itemsPayload);
    const ordenId = this.ordenId!;
    const ordenIri = `/api/ordenes/${ordenId}`;

    const deleteRequests = deletedItemIds.map((id) => this.ordenItemService.delete(id));
    const updateRequests = itemsToUpdate.map((item) =>
      this.ordenItemService.update(item.id!, this.stripItemId(item))
    );
    const createRequests = itemsToCreate.map((item) =>
      this.ordenItemService.create({ ...item, orden: ordenIri })
    );

    // IMPORTANTE: Procesar items PRIMERO (delete, update, create) antes del PATCH a la orden.
    // Esto evita que el backend rechace el PATCH por validación cascade de items
    // que aún tienen datos inválidos (como cantidad null).
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => (updateRequests.length ? forkJoin(updateRequests) : of([]))),
      switchMap(() => (createRequests.length ? forkJoin(createRequests) : of([]))),
      switchMap(() => this.ordenService.update(ordenId, ordenPayload)),
      switchMap(() => this.ordenService.getById(ordenId)),
      map((orden) => {
        this.itemsEmisionFacade.clearDeletedItemIds();
        this.patchOrdenForm(orden);
        return orden;
      }),
      finalize(() => this.itemsLoading.set(false))
    );
  }

  private splitItemsPayload(items: OrdenItem[]): { itemsToCreate: OrdenItem[]; itemsToUpdate: OrdenItem[] } {
    const itemsToCreate: OrdenItem[] = [];
    const itemsToUpdate: OrdenItem[] = [];
    items.forEach((item) => {
      if (item.id) {
        itemsToUpdate.push(item);
      } else {
        itemsToCreate.push(item);
      }
    });
    return { itemsToCreate, itemsToUpdate };
  }

  private stripItemId(item: OrdenItem): OrdenItem {
    const { id, ...rest } = item;
    return rest;
  }

  private resetFormState(): void {
    this.itemsEmisionFacade.clearDeletedItemIds();
    this.validationSummary.set([]);
    this.itemsFormArray.clear();
    this.confirmedTotals.set(null);
    this.ordenForm.reset({
      numero: '',
      fechaIngreso: new Date(),
      clientePrincipal: null,
      anunciante: null,
      productor: null,
      facturacionRol: 'principal',
      tipoOrden: null,
      estado: 'borrador',
      moneda: 'Pesos',
      tarifa: null,
      vigenciaDesde: null,
      vigenciaHasta: null,
      descuento: 0,
      porcentajeComision: 0,
      esqueletoNombre: null,
      observaciones: null
    });
  }

  exportarOrden(): void {
    if (!this.ordenId) return;
    this.ordenService.exportJson(this.ordenId).subscribe({
      next: (payload) => {
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `orden-${this.ordenForm.get('numero')?.value || this.ordenId}.json`;
        link.click();
        URL.revokeObjectURL(url);
        this.messageService.add({
          severity: 'success',
          summary: 'Exportación lista',
          detail: 'Se descargó el JSON de la orden.',
          life: 3000
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo exportar la orden.',
          life: 3000
        });
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/ordenes']);
  }

  /**
   * Precarga los 3 tipos de clientes al iniciar la pantalla
   */
  private preloadClientes(): void {
    if (this.loadedOnce.clientePrincipal && this.loadedOnce.anunciante && this.loadedOnce.productor) {
      return;
    }

    this.loadingClientes.clientePrincipal = true;
    this.loadingClientes.anunciante = true;
    this.loadingClientes.productor = true;
    this.cdr.markForCheck();

    forkJoin({
      agencias: this.clienteLookupService.getClientesLookup({ tipoCliente: 'agencia' }),
      anunciantes: this.clienteLookupService.getClientesLookup({ tipoCliente: 'anunciante' }),
      productores: this.clienteLookupService.getClientesLookup({ tipoCliente: 'productor' })
    })
      .pipe(
        take(1),
        finalize(() => {
          this.loadingClientes.clientePrincipal = false;
          this.loadingClientes.anunciante = false;
          this.loadingClientes.productor = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ({ agencias, anunciantes, productores }) => {
          this.clientePrincipalOptions = agencias.map(opt => opt.cliente);
          this.anuncianteOptions = anunciantes.map(opt => opt.cliente);
          this.productorOptions = productores.map(opt => opt.cliente);
          this.clientePrincipalSuggestions = [...this.clientePrincipalOptions];
          this.anuncianteSuggestions = [...this.anuncianteOptions];
          this.productorSuggestions = [...this.productorOptions];
          this.loadedOnce.clientePrincipal = true;
          this.loadedOnce.anunciante = true;
          this.loadedOnce.productor = true;
          console.debug('[Cliente preload]', {
            principal: this.clientePrincipalOptions.length,
            anunciante: this.anuncianteOptions.length,
            productor: this.productorOptions.length
          });
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error al precargar clientes:', error);
        }
      });
  }

  /**
   * Configura debounce para las búsquedas en los autocompletes
   */
  private setupSearchDebounce(): void {
    // Cliente Principal (Agencias)
    this.clientePrincipalSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((term: string) => {
        this.loadingClientes.clientePrincipal = true;
        this.cdr.markForCheck();
        this.clienteLookupService
          .getClientesLookup({ tipoCliente: 'agencia', term })
          .pipe(
            take(1),
            finalize(() => {
              this.loadingClientes.clientePrincipal = false;
              this.cdr.markForCheck();
            }),
            takeUntil(this.destroy$)
          )
          .subscribe({
            next: (options) => {
              this.clientePrincipalOptions = options.map(opt => opt.cliente);
              this.clientePrincipalSuggestions = [...this.clientePrincipalOptions];
            },
            error: () => {
              this.clientePrincipalOptions = [];
              this.clientePrincipalSuggestions = [];
            }
          });
      });

    // Anunciantes
    this.anuncianteSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((term: string) => {
        const productorId = this.getProductorId();
        const agenciaId = this.getAgenciaId();
        const padreId = productorId || agenciaId;
        
        this.loadingClientes.anunciante = true;
        this.cdr.markForCheck();
        this.clienteLookupService
          .getClientesLookup({
            tipoCliente: 'anunciante',
            agenciaProductorId: padreId || null,
            term
          })
          .pipe(
            take(1),
            catchError(() => of([])),
            finalize(() => {
              this.loadingClientes.anunciante = false;
              this.cdr.markForCheck();
            }),
            takeUntil(this.destroy$)
          )
          .subscribe((options) => {
            if (options.length === 0 && padreId) {
              this.clienteLookupService
                .getClientesLookup({
                  tipoCliente: 'anunciante',
                  agenciaProductorId: null,
                  term
                })
                .pipe(take(1), catchError(() => of([])), takeUntil(this.destroy$))
                .subscribe((fallbackOptions) => {
                  this.anuncianteOptions = fallbackOptions.map(opt => opt.cliente);
                  this.anuncianteSuggestions = [...this.anuncianteOptions];
                  this.cdr.markForCheck();
                });
              return;
            }

            this.anuncianteOptions = options.map(opt => opt.cliente);
            this.anuncianteSuggestions = [...this.anuncianteOptions];
            this.cdr.markForCheck();
          });
      });

    // Productores
    this.productorSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((term: string) => {
        this.loadingClientes.productor = true;
        this.cdr.markForCheck();
        this.clienteLookupService
          .getClientesLookup({ tipoCliente: 'productor', term })
          .pipe(
            take(1),
            finalize(() => {
              this.loadingClientes.productor = false;
              this.cdr.markForCheck();
            }),
            takeUntil(this.destroy$)
          )
          .subscribe({
            next: (options) => {
              this.productorOptions = options.map(opt => opt.cliente);
              this.productorSuggestions = [...this.productorOptions];
            },
            error: () => {
              this.productorOptions = [];
              this.productorSuggestions = [];
            }
          });
      });
  }

  /**
   * Maneja la búsqueda en los autocompletes (con debounce)
   */
  searchClientes(event: any, rol: ClienteRolKey): void {
    const query = (event?.query ?? '').trim();
    if (!query) {
      if (rol === 'principal') {
        if (this.clientePrincipalOptions.length > 0) {
          this.clientePrincipalSuggestions = [...this.clientePrincipalOptions];
          this.loadingClientes.clientePrincipal = false;
          this.cdr.markForCheck();
          return;
        }
      }
      if (rol === 'productor') {
        if (this.productorOptions.length > 0) {
          this.productorSuggestions = [...this.productorOptions];
          this.loadingClientes.productor = false;
          this.cdr.markForCheck();
          return;
        }
      }
      if (rol === 'anunciante') {
        if (this.anuncianteOptions.length > 0) {
          this.anuncianteSuggestions = [...this.anuncianteOptions];
          this.loadingClientes.anunciante = false;
          this.cdr.markForCheck();
          return;
        }
      }
      if (rol === 'anunciante' && !this.loadedOnce.anunciante) {
        this.refreshAnunciantes();
      }
      if ((rol === 'principal' && !this.loadedOnce.clientePrincipal)
        || (rol === 'productor' && !this.loadedOnce.productor)) {
        this.preloadClientes();
      }
      return;
    }

    if (rol === 'principal') {
      this.clientePrincipalSearch$.next(query);
    } else if (rol === 'anunciante') {
      this.anuncianteSearch$.next(query);
    } else {
      this.productorSearch$.next(query);
    }
  }

  searchAnunciantes(event: any): void {
    const query = (event?.query ?? '').trim();
    const productorId = this.getProductorId();
    const agenciaId = this.getAgenciaId();
    const padreId = productorId || agenciaId;

    this.loadingClientes.anunciante = true;
    this.cdr.markForCheck();

    this.clienteLookupService
      .getClientesLookup({
        tipoCliente: 'anunciante',
        agenciaProductorId: padreId || null,
        term: query,
        page: 1,
        itemsPerPage: 50
      })
      .pipe(
        take(1),
        catchError(() => of([])),
        finalize(() => {
          this.loadingClientes.anunciante = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((options) => {
        if (options.length === 0 && padreId) {
          this.clienteLookupService
            .getClientesLookup({
              tipoCliente: 'anunciante',
              agenciaProductorId: null,
              term: query,
              page: 1,
              itemsPerPage: 50
            })
            .pipe(take(1), catchError(() => of([])), takeUntil(this.destroy$))
            .subscribe((fallbackOptions) => {
              const clientes = fallbackOptions.map(opt => opt.cliente);
              this.anuncianteOptions = clientes;
              this.anuncianteSuggestions = [...clientes];
              this.loadedOnce.anunciante = true;
              this.cdr.markForCheck();
            });
          return;
        }

        const clientes = options.map(opt => opt.cliente);
        this.anuncianteOptions = clientes;
        this.anuncianteSuggestions = [...clientes];
        this.loadedOnce.anunciante = true;
        this.cdr.markForCheck();
      });
  }

  /**
   * Obtiene el ID del productor seleccionado
   */
  private getProductorId(): number | string | null {
    const productor = this.ordenForm.get('productor')?.value;
    if (!productor) return null;
    if (typeof productor === 'object' && Array.isArray(productor.tipoCliente)) {
      if (!productor.tipoCliente.includes('productor')) {
        return null;
      }
    }
    const id = this.extractId(productor);
    return id !== null ? id : null;
  }

  /**
   * Obtiene el ID de la agencia/cliente principal seleccionado
   */
  private getAgenciaId(): number | string | null {
    const clientePrincipal = this.ordenForm.get('clientePrincipal')?.value;
    if (!clientePrincipal) return null;
    if (typeof clientePrincipal === 'object' && Array.isArray(clientePrincipal.tipoCliente)) {
      if (!clientePrincipal.tipoCliente.includes('agencia')) {
        return null;
      }
    }
    const id = this.extractId(clientePrincipal);
    return id !== null ? id : null;
  }

  /**
   * Actualiza los anunciantes cuando cambia el productor o la agencia
   */
  private refreshAnunciantes(): void {
    const productorId = this.getProductorId();
    const agenciaId = this.getAgenciaId();
    const padreId = productorId || agenciaId;
    
    const anuncianteActual = this.ordenForm.get('anunciante')?.value;
    const anuncianteId = anuncianteActual ? this.extractId(anuncianteActual) : null;
    
    this.loadingClientes.anunciante = true;
    this.cdr.markForCheck();
    this.clienteLookupService
      .getClientesLookup({
        tipoCliente: 'anunciante',
        agenciaProductorId: padreId || null
      })
      .pipe(
        take(1),
        catchError(() => of([])),
        finalize(() => {
          this.loadingClientes.anunciante = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((options) => {
        if (options.length === 0 && padreId) {
          this.fetchAnunciantesFallback(anuncianteId, anuncianteActual);
          return;
        }
        this.applyAnuncianteOptions(options, anuncianteId, anuncianteActual, padreId);
      });
  }

  private fetchAnunciantesFallback(
    anuncianteId: number | string | null,
    anuncianteActual: any
  ): void {
    this.clienteLookupService
      .getClientesLookup({
        tipoCliente: 'anunciante',
        agenciaProductorId: null
      })
      .pipe(take(1), catchError(() => of([])), takeUntil(this.destroy$))
      .subscribe((options) => {
        this.applyAnuncianteOptions(options, anuncianteId, anuncianteActual, null);
      });
  }

  private applyAnuncianteOptions(
    options: { cliente: Cliente }[],
    anuncianteId: number | string | null,
    anuncianteActual: any,
    padreId: number | string | null
  ): void {
    const clientes = options.map(opt => opt.cliente);
    this.anuncianteOptions = clientes;
    this.anuncianteSuggestions = [...clientes];
    this.loadedOnce.anunciante = true;

    if (anuncianteId && padreId && clientes.length > 0) {
      const existe = clientes.some(c => {
        const cId = this.extractId(c);
        return cId !== null && cId === anuncianteId;
      });

      if (!existe) {
        const anuncianteObj = typeof anuncianteActual === 'object' ? anuncianteActual : null;
        if (anuncianteObj?.agenciaProductor) {
          const agenciaProductorId = this.extractId(anuncianteObj.agenciaProductor);
          if (agenciaProductorId !== padreId) {
            this.ordenForm.patchValue({ anunciante: null }, { emitEvent: false });
            this.messageService.add({
              severity: 'info',
              summary: 'Anunciante actualizado',
              detail: 'El anunciante seleccionado no corresponde al productor/agencia elegido.',
              life: 3000
            });
          }
        }
      }
    }

    this.cdr.markForCheck();
  }


  private buildOrdenClientes(formValue: any): OrdenCliente[] {
    const clientes: OrdenCliente[] = [];
    const roles: { key: ClienteRolKey; rol: string }[] = [
      { key: 'principal', rol: 'principal' },
      { key: 'anunciante', rol: 'anunciante' },
      { key: 'productor', rol: 'productor' }
    ];
    const facturacionRol = formValue.facturacionRol ?? null;

    roles.forEach(({ key, rol }) => {
      const clienteValue = formValue[key === 'principal' ? 'clientePrincipal' : key];
      const clienteIri = this.buildIri(clienteValue, 'clientes');
      if (clienteIri) {
        const payload: OrdenCliente = {
          rol: rol as any,
          cliente: clienteIri,
          clienteFacturado: facturacionRol === rol,
          porcentajeFacturacion: facturacionRol === rol ? 100 : undefined
        };
        if (this.ordenClienteIds[rol]) {
          payload.id = this.ordenClienteIds[rol];
        }
        clientes.push(payload);
      }
    });

    return clientes;
  }

  private buildItemsPayload(): OrdenItem[] {
    const deletedIds = new Set(this.itemsEmisionFacade.getDeletedItemIds());
    return this.itemsFormArray.controls
      .map((control, index) => ({ control: control as FormGroup, index }))
      .filter(({ control }) => {
        const idValue = control.get('id')?.value;
        const numericId = idValue ? Number(idValue) : null;
        if (numericId && deletedIds.has(numericId)) {
          return false;
        }
        return !this.isItemEmpty(control);
      })
      .map(({ control, index }) => {
        this.itemsEmisionFacade.ensureTipoProductoForGroup(control);
        const value = control.value;
        const tipoCodigo = this.getTipoProductoCodigo(value.tipoProducto);
        return {
          id: value.id || undefined,
          tipoProducto: this.buildIri(value.tipoProducto, 'tipo-productos') || undefined,
          programa: this.buildIri(value.programa, 'programas') || undefined,
          material: this.buildIri(value.material, 'materials') || undefined,
          diaTipo: value.diaTipo ?? null,
          frecuencia: tipoCodigo === 'AIRE_SPOT' ? (value.frecuencia ?? null) : null,
          horaDesde: this.formatTime(value.horaDesde),
          horaHasta: this.formatTime(value.horaHasta),
          duracionSegundos: value.duracionSegundos ?? null,
          avisos: value.avisos ?? 0,
          cantidad: this.normalizeCantidad(value.cantidad),
          bonificado: value.bonificado ?? false,
          motivoBonificacion: value.motivoBonificacion ?? null,
          formatoDigital: this.buildIri(value.formatoDigital, 'formatos-digitales') || undefined,
          ubicacionDigital: this.buildIri(value.ubicacionDigital, 'ubicaciones-digitales') || undefined,
          fechaDesde: this.formatDateTime(value.fechaDesde),
          fechaHasta: this.formatDateTime(value.fechaHasta),
          plataformaRedes: value.plataformaRedes ?? null,
          tituloContenido: value.tituloContenido ?? null,
          eventoNombre: value.eventoNombre ?? null,
          fechaEvento: this.formatDateTime(value.fechaEvento),
          textoALeer: value.textoALeer ?? null,
          subtotalManual: value.subtotalManual ?? null,
          ordenIndex: index,
          observacion: value.observacion || null
        };
      });
  }

  private isItemEmpty(group: FormGroup): boolean {
    const value = group.value;
    const hasAnyField = !!(
      value.programa ||
      value.material ||
      value.diaTipo ||
      value.frecuencia ||
      value.horaDesde ||
      value.horaHasta ||
      value.duracionSegundos ||
      value.avisos ||
      value.cantidad ||
      value.formatoDigital ||
      value.ubicacionDigital ||
      value.fechaDesde ||
      value.fechaHasta ||
      value.plataformaRedes ||
      value.tituloContenido ||
      value.eventoNombre ||
      value.fechaEvento ||
      value.textoALeer ||
      value.subtotalManual
    );
    return !value.id && !hasAnyField;
  }

  private buildIri(value: any, resource: string): string | null {
    if (!value) return null;
    if (typeof value === 'string') {
      if (value.startsWith('/api/')) {
        return value;
      }
      const match = value.match(/\/(\d+)$/);
      if (match) {
        return `/api/${resource}/${match[1]}`;
      }
      if (/^\d+$/.test(value)) {
        return `/api/${resource}/${value}`;
      }
    }
    if (typeof value === 'number') {
      return `/api/${resource}/${value}`;
    }
    if (value.id) {
      return `/api/${resource}/${value.id}`;
    }
    return null;
  }

  private normalizeCantidad(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private formatDateTime(value: any): string | null {
    if (!value) return null;
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value.toISOString();
    }
    return value;
  }

  private formatTime(value: any): string | null {
    if (!value) return null;
    if (value instanceof Date && !isNaN(value.getTime())) {
      const hours = value.getHours().toString().padStart(2, '0');
      const minutes = value.getMinutes().toString().padStart(2, '0');
      const seconds = value.getSeconds().toString().padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    }
    return value;
  }

  private getTipoProductoCodigo(value: any): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'object' && 'codigo' in value) {
      return (value as any).codigo ?? null;
    }
    return null;
  }

  private updateTotals(): void {
    const descuento = Number(this.ordenForm.get('descuento')?.value || 0);
    const porcentajeComision = Number(this.ordenForm.get('porcentajeComision')?.value || 0);

    let duracionTotal = 0;
    let bruto = 0;

    this.itemsFormArray.controls.forEach(control => {
      const itemGroup = control as FormGroup;
      const item = itemGroup.value;

      // Usar el método del facade que tiene la lógica completa de cálculo
      const subtotal = this.itemsEmisionFacade.getSubtotal(itemGroup);

      const avisos = Number(item.avisos || 0);
      const duracion = Number(item.duracionSegundos || 0);

      const tipoProductoCodigo = this.getTipoProductoCodigo(item.tipoProducto);
      if (tipoProductoCodigo === 'AIRE_SPOT') {
        const duracionPreview = Number(item.duracionSegundosPreview || 0);
        duracionTotal += duracionPreview > 0 ? duracionPreview : avisos * duracion;
      }
      bruto += subtotal;
    });

    const descuentoMonto = bruto * (descuento / 100);
    const neto = Math.max(0, bruto - descuentoMonto);
    const comision = neto * (porcentajeComision / 100);
    this.totals.set({ duracionTotal, bruto, descuento, descuentoMonto, neto, comision });
  }

  private ensureFacturacionSelection(): void {
    const current = this.ordenForm.get('facturacionRol')?.value ?? null;
    const options = this.facturacionOptions();
    const currentOption = options.find(option => option.value === current);
    if (currentOption && !currentOption.disabled) {
      return;
    }

    const fallback = options.find(option => !option.disabled)?.value ?? null;
    this.ordenForm.get('facturacionRol')?.setValue(fallback, { emitEvent: false });
  }

  facturacionOptions(): Array<{ label: string; value: string; disabled?: boolean }> {
    const principal = this.ordenForm.get('clientePrincipal')?.value ?? null;
    const anunciante = this.ordenForm.get('anunciante')?.value ?? null;
    const productor = this.ordenForm.get('productor')?.value ?? null;

    return [
      {
        label: `Anunciante: ${this.getClienteDisplay(anunciante)}`,
        value: 'anunciante',
        disabled: !anunciante
      },
      {
        label: `Productor: ${this.getClienteDisplay(productor)}`,
        value: 'productor',
        disabled: !productor
      },
      {
        label: `Cliente Principal: ${this.getClienteDisplay(principal)}`,
        value: 'principal',
        disabled: !principal
      }
    ];
  }

  private getClienteDisplay(cliente: Cliente | string | null): string {
    if (!cliente) {
      return 'Sin asignar';
    }
    if (typeof cliente === 'string') {
      return `#${this.extractId(cliente) ?? cliente}`;
    }
    return cliente.nombreFantasia || cliente.razonSocial || `#${cliente.id ?? '-'}`;
  }

  facturacionResumen(): { ok: boolean; text: string } {
    const rol = this.ordenForm.get('facturacionRol')?.value ?? null;
    const cliente = this.resolveClienteByRol(rol);
    if (!rol || !cliente) {
      return { ok: false, text: '' };
    }

    return {
      ok: true,
      text: `Se factura a: ${this.getRolLabel(rol)} — ${this.getClienteDisplay(cliente)}`
    };
  }

  private resolveClienteByRol(rol: string | null): Cliente | string | null {
    if (rol === 'principal') {
      return this.ordenForm.get('clientePrincipal')?.value ?? null;
    }
    if (rol === 'anunciante') {
      return this.ordenForm.get('anunciante')?.value ?? null;
    }
    if (rol === 'productor') {
      return this.ordenForm.get('productor')?.value ?? null;
    }
    return null;
  }

  private getRolLabel(rol: string | null): string {
    if (rol === 'principal') return 'Cliente Principal';
    if (rol === 'anunciante') return 'Anunciante';
    if (rol === 'productor') return 'Productor';
    return 'Sin definir';
  }

  private resolveFacturacionRolDefault(
    orden: Orden,
    principal?: OrdenCliente,
    anunciante?: OrdenCliente,
    productor?: OrdenCliente
  ): string | null {
    const facturado = (orden.ordenClientes || []).find(cliente => cliente.clienteFacturado);
    if (facturado?.rol) {
      return facturado.rol;
    }

    if (principal?.cliente) return 'principal';
    if (anunciante?.cliente) return 'anunciante';
    if (productor?.cliente) return 'productor';

    return null;
  }

  private handleValidationErrors(error: unknown): void {
    const normalized = this.apiErrorService.extractViolations(error);
    if (normalized.length === 0) {
      return;
    }

    this.apiErrorService.applyViolationsToForm(this.ordenForm, normalized);
    this.validationSummary.set(
      this.apiErrorService.buildUserSummary(normalized, {
        resolveItemLabel: (index) => this.buildItemLabel(index)
      })
    );

    this.messageService.add({
      severity: 'error',
      summary: 'Faltan datos',
      detail: 'Revisá los campos marcados antes de guardar.',
      life: 5000
    });

    this.focusFromErrors(normalized);
  }

  private focusFromErrors(errors: NormalizedFormError[]): void {
    const firstError = errors[0];
    if (!firstError) {
      this.apiErrorService.focusFirstInvalid(this.formContainer?.nativeElement);
      return;
    }

    const match = /^items\[(\d+)\](?:\.(.+))?$/.exec(firstError.path);
    if (match) {
      const index = Number(match[1]);
      const field = match[2] ?? null;
      this.openItemsTabByIndex(index);
      setTimeout(() => this.focusItemField(index, field), 0);
      return;
    }

    this.apiErrorService.focusFirstInvalid(this.formContainer?.nativeElement);
  }

  private openItemsTabByIndex(index: number): void {
    const group = this.itemsFormArray.at(index) as FormGroup | null;
    if (!group || !this.itemsSection) {
      return;
    }
    const tipo = this.itemsEmisionFacade.resolveItemTypeFromGroup(group);
    if (tipo) {
      this.itemsSection.selectType(tipo);
    }
  }

  private focusItemField(index: number, field?: string | null): void {
    const container = this.formContainer?.nativeElement;
    if (!container) {
      return;
    }

    const row = container.querySelector<HTMLElement>(`.item-row[data-item-index="${index}"]`);
    if (!row) {
      return;
    }

    if (field) {
      const fieldHost = row.querySelector<HTMLElement>(`[formcontrolname="${field}"]`);
      const focusTarget = fieldHost?.querySelector<HTMLElement>('input, textarea, select') ?? fieldHost;
      if (focusTarget) {
        focusTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        focusTarget.focus();
        return;
      }
    }

    const fallback = row.querySelector<HTMLElement>('input, textarea, select');
    if (fallback) {
      fallback.scrollIntoView({ behavior: 'smooth', block: 'center' });
      fallback.focus();
    }
  }

  private collectFormValidationErrors(): string[] {
    const errors: string[] = [];
    const fieldLabels: Record<string, string> = {
      numero: 'Número de Orden',
      fechaIngreso: 'Fecha de Ingreso',
      vigenciaDesde: 'Vigencia Desde',
      vigenciaHasta: 'Vigencia Hasta',
      clientePrincipal: 'Cliente Principal',
      anunciante: 'Anunciante',
      productor: 'Productor',
      facturacionRol: 'Facturar a',
      porcentajeComision: 'Porcentaje de Comisión',
      descuento: 'Descuento',
      tipoOrden: 'Tipo de Orden',
      moneda: 'Moneda',
      tarifa: 'Tarifa',
      programa: 'Programa',
      material: 'Material',
      diaTipo: 'Día',
      horaDesde: 'Hora Desde',
      horaHasta: 'Hora Hasta',
      duracionSegundos: 'Duración',
      avisos: 'Avisos',
      ubicacionDigital: 'Ubicación Digital',
      formatoDigital: 'Formato Digital',
      fechaDesde: 'Fecha Desde',
      fechaHasta: 'Fecha Hasta',
      plataformaRedes: 'Plataforma',
      cantidad: 'Cantidad',
      tituloContenido: 'Título',
      eventoNombre: 'Evento',
      fechaEvento: 'Fecha del Evento'
    };

    // Check main form fields
    Object.keys(this.ordenForm.controls).forEach(key => {
      if (key === 'items') return;
      const control = this.ordenForm.get(key);
      if (control?.invalid && control?.errors) {
        const label = fieldLabels[key] || key;
        if (control.errors['required']) {
          errors.push(`${label} es requerido`);
        }
      }
    });

    // Check items
    this.itemsFormArray.controls.forEach((control, index) => {
      const group = control as FormGroup;
      const itemLabel = this.buildItemLabel(index);
      const tipo = this.itemsEmisionFacade.resolveItemTypeFromGroup(group);
      const requiredFields = tipo ? this.itemsEmisionFacade.getRequiredFields(tipo) : [];

      requiredFields.forEach(fieldName => {
        const fieldControl = group.get(fieldName);
        if (fieldControl?.invalid && fieldControl?.errors) {
          const label = fieldLabels[fieldName] || fieldName;
          if (fieldControl.errors['required']) {
            errors.push(`${itemLabel}: ${label} es requerido`);
          }
        }
      });
    });

    return errors;
  }

  private buildItemLabel(index: number): string {
    const group = this.itemsFormArray.at(index) as FormGroup | null;
    const tipo = group ? this.itemsEmisionFacade.resolveItemTypeFromGroup(group) : null;
    const labelMap: Record<string, string> = {
      AIRE_SPOT: 'Item Aire Spot',
      WEB_BANNER: 'Item Banner Web',
      REDES_PIEZA: 'Item Redes',
      PUBLINOTA: 'Item Publinota',
      COBERTURA_EVENTO: 'Item Cobertura'
    };
    const label = tipo ? (labelMap[tipo] ?? 'Item') : 'Item';
    return `${label} #${index + 1}`;
  }

  private loadTarifas(): void {
    const params = new HttpParams()
      .set('active', 'true')
      .set('pagination', 'false');

    this.tarifaService.getAll(params).subscribe({
      next: (resp: any) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        this.tarifaOptions.set(members);
        this.ensureTarifaOption(this.tarifaFromOrden);

        const defaultTarifa = members.find((tarifa: Tarifa) => tarifa.esDefault);
        if (defaultTarifa && !this.ordenForm.get('tarifa')?.value) {
          this.ordenForm.patchValue({ tarifa: defaultTarifa.id }, { emitEvent: false });
        }
      },
      error: () => {
        this.tarifaOptions.set([]);
      }
    });
  }

  private syncTarifaControlState(estado: OrdenEstado | null): void {
    const control = this.ordenForm.get('tarifa');
    if (!control) {
      return;
    }
    const shouldDisable = estado !== 'borrador';
    if (shouldDisable && control.enabled) {
      control.disable({ emitEvent: false });
    }
    if (!shouldDisable && control.disabled) {
      control.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  private extractId(value: any): number | string | null {
    if (!value) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Si es un string numérico directo
      if (/^\d+$/.test(value)) {
        return Number(value);
      }
      // Si es un IRI (/api/resource/id)
      const match = value.match(/\/(\d+)$/);
      if (match) {
        return Number(match[1]);
      }
      return value; // Retornar el string si no es numérico ni IRI
    }
    if (typeof value === 'object' && value['@id']) {
      const match = String(value['@id']).match(/\/(\d+)$/);
      if (match) {
        return Number(match[1]);
      }
      return value['@id'];
    }
    if (value.id !== undefined && value.id !== null) {
      return typeof value.id === 'number' ? value.id : String(value.id);
    }
    return null;
  }

  private ensureTarifaOption(tarifa: Tarifa | null): void {
    if (!tarifa || !tarifa.id) {
      return;
    }
    const current = this.tarifaOptions();
    const exists = current.some(opt => opt.id === tarifa.id);
    if (!exists) {
      this.tarifaOptions.set([...current, tarifa]);
    }
  }
}
